import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormApi } from 'final-form';
import { Box } from '@mui/material';

import { LoadingOverlay } from '@occc-app/design-system';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { OfferFilter } from '@occc/graphql';
import { Flow_Types } from '@occc-app/offerHelpers/types';

import { ConfigureFormContent } from './ConfigureFormContent';

import { validateConfiguration } from '../../utils';
import { OfferConfigureFormInitialValues, OfferConfigureFormValues } from '../../types';

type ConfigureFormProps = {
  onSubmit: (
    values: OfferConfigureFormValues,
    form: FormApi<OfferConfigureFormValues, OfferConfigureFormInitialValues>,
  ) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: OfferConfigureFormInitialValues;
  makeOffersFilter: (search: string) => OfferFilter;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  makeOffersFilter,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const renderForm: FormRenderProps<any>['render'] = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      form,
      dirty,
    }: FormRenderProps<OfferConfigureFormValues>) => {
      return (
        <ConfigureFormContent
          onCancelClick={onCancelClick}
          dirty={dirty}
          isFormEditable={isFormEditable}
          currentFlowType={currentFlowType}
          submitting={submitting}
          onNextStepButtonClick={onNextStepButtonClick}
          form={form}
          values={values}
          makeOffersFilter={makeOffersFilter}
          handleSubmit={handleSubmit}
        />
      );
    },
    [isFormEditable, currentFlowType, onNextStepButtonClick, onCancelClick, makeOffersFilter],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<OfferConfigureFormValues, OfferConfigureFormInitialValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
            setFieldValue: (args, state, utils) => {
              // name, value
              utils.changeValue(state, args[0], () => args[1]);
            },
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
